import { AutomationProject, FreeGrantBalance } from "@/types/automation";
import { appFetch } from "./appClient";
import { apiVersions, armEndpoint, getLogicEndpoint } from "./endpoints";

export type AppNameValidationResult = {
  isValid: boolean;
  error: string;
};

/**
 * Gets an automation project.
 */
export const getAutomationProject = async (projectName: string, getToken: () => Promise<string | null>): Promise<AutomationProject> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get Otto token');
  }

  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}?api-version=${apiVersions.otto}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;
    try {
      const errorResponse = errorText ? JSON.parse(errorText) : null;
      const error = errorResponse?.error ?? errorResponse;
      errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
    } catch {
      errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
    }
    console.error('GET request failed:', response.status, errorMessage);
    throw new Error(errorMessage);
  }

  const project = await response.json();
  return project;
};

/**
 * Gets a list of automation projects in a subscription.
 * Uses the ARM API and navigates through all nextLinks to get the complete list.
 * 
 * @param subscriptionId - The Azure subscription ID
 * @param getToken - Function to get the ARM access token
 * @returns Array of automation project summaries with name, id, location, and status
 */
export type AutomationProjectSummary = {
  name: string;
  id: string;
  location: string;
  status: string;
};

export const listAutomationProjects = async (
  subscriptionId: string,
  getToken: () => Promise<string | null>
): Promise<AutomationProjectSummary[]> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get ARM token');
  }

  const projects: AutomationProjectSummary[] = [];
  let nextLink: string | null = `${armEndpoint}/subscriptions/${subscriptionId}/providers/Microsoft.Logic/automationProjects?api-version=${apiVersions.otto}`;

  while (nextLink) {
    const response = await fetch(nextLink, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;
      try {
        const errorResponse = errorText ? JSON.parse(errorText) : null;
        const error = errorResponse?.error ?? errorResponse;
        errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
      } catch {
        errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
      }
      console.error('Failed to list automation projects:', response.status, errorMessage);
      throw new Error(errorMessage);
    }

    type ProjectResponse = { name: string; id: string; location: string; properties?: { provisioningState?: string } };
    const data: { value?: ProjectResponse[]; nextLink?: string } = await response.json();
    const pageProjects = (data.value ?? []).map((project) => ({
      name: project.name,
      id: project.id,
      location: project.location,
      status: project.properties?.provisioningState ?? 'Unknown',
    }));

    projects.push(...pageProjects);
    nextLink = data.nextLink ?? null;
  }

  return projects;
};

/**
 * Creates an automation project in a subscription using the ARM API.
 * 
 * @param subscriptionId - The Azure subscription ID
 * @param resourceGroup - The Azure resource group name
 * @param name - The name of the automation project to create
 * @param location - The Azure region for the project (e.g., 'eastus')
 * @param getToken - Function to get the ARM access token
 * @returns The created automation project resource
 */
export type CreateAutomationProjectResult = {
  id: string;
  name: string;
  type: string;
  location: string;
  properties?: {
    provisioningState?: string;
  };
};

export const createAutomationProject = async (
  subscriptionId: string,
  resourceGroup: string,
  name: string,
  location: string,
  getToken: () => Promise<string | null>
): Promise<CreateAutomationProjectResult> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get ARM token');
  }

  const resourceId = `/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.Logic/automationProjects/${name}`;
  const url = `${armEndpoint}${resourceId}?api-version=${apiVersions.otto}`;

  const payload = {
    id: resourceId,
    name,
    type: 'Microsoft.Logic/automationProjects',
    location,
    identity: { type: 'SystemAssigned' },
    properties: { description: `Automation Project - ${name}` }
  };

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;
    try {
      const errorResponse = errorText ? JSON.parse(errorText) : null;
      const error = errorResponse?.error ?? errorResponse;
      errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
    } catch {
      errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
    }
    console.error('Failed to create automation project:', response.status, errorMessage);
    throw new Error(errorMessage);
  }

  return await response.json();
};

// ============================================================================
// Managed models
// ============================================================================

/**
 * A curated Microsoft-managed AI model deployment available to the selected
 * application's runtime.
 *
 * `id` matches the AOAI deployment ID that must be written into an agent's
 * `deploymentId` field when using the `ManagedModel` connection type.
 * `displayName` is a human-friendly label for the model dropdown.
 */
export type ManagedModel = {
  id: string;
  displayName: string;
};

/**
 * Response shape shared by the runtime and control-plane managed-model endpoints.
 * An empty `models` array is authoritative and signals that Microsoft-managed
 * models are unavailable to the selected runtime.
 */
export type ManagedModelsResponse = {
  models: ManagedModel[];
};

/**
 * Fetches the selected runtime's curated Microsoft-managed model list through
 * the application data-plane gateway. BPM resolves and authenticates the
 * runtime server-side, so runtime credentials never enter the browser.
 */
export const listApplicationManagedModels = async (
  projectName: string,
  applicationName: string,
  accessToken: string,
): Promise<ManagedModel[]> => {
  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(applicationName)}/managedModels?api-version=${apiVersions.otto}`;
  const data = await appFetch<ManagedModelsResponse>(url, accessToken, {
    headers: {
      Accept: 'application/json',
    },
  });
  return data.models ?? [];
};

/**
 * Fetches the control-plane copy of the curated Microsoft-managed model list.
 * This is retained only as a compatibility fallback for runtimes that are not
 * available or do not yet expose the runtime endpoint.
 */
export const listControlPlaneManagedModels = async (
  projectName: string,
  getToken: () => Promise<string | null>,
): Promise<ManagedModel[]> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get Otto token');
  }

  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/managedModels?api-version=${apiVersions.otto}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;
    try {
      const errorResponse = errorText ? JSON.parse(errorText) : null;
      const error = errorResponse?.error ?? errorResponse;
      errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
    } catch {
      errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
    }
    console.error('Failed to list managed models:', response.status, errorMessage);
    throw new Error(errorMessage);
  }

  const data: ManagedModelsResponse = await response.json();
  return data.models ?? [];
};

// ============================================================================
// Free grant balances
// ============================================================================

/**
 * Fetches the free-grant meter balances for a project via the ARM data-plane
 * endpoint `GET {armResourceId}/freeGrantBalances?api-version=...`.
 *
 * Only meaningful for free-grant projects (`getIsFreeProject(project) === true`);
 * callers should gate the request on that flag.
 *
 * The control plane returns a bare JSON array of {@link FreeGrantBalance} items
 * (no `value` envelope). We still tolerate a `{ value: [...] }` wrapper in case
 * the shape is normalized later.
 *
 * @param projectResourceId - Full ARM resource id of the project
 *   (`/subscriptions/{sub}/resourceGroups/{rg}/providers/Microsoft.Logic/automationProjects/{name}`).
 * @param getToken - Function to get the ARM access token.
 */
export const getFreeGrantBalances = async (
  projectResourceId: string,
  getToken: () => Promise<string | null>,
): Promise<FreeGrantBalance[]> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get ARM token');
  }

  const resourcePath = projectResourceId.startsWith('/') ? projectResourceId : `/${projectResourceId}`;
  // Use the shared `armEndpoint` (not `getArmBaseUrl()`) so this call honors the
  // same ARM host overrides as the rest of projectService (`VITE_ARM_ENDPOINT` /
  // `window.__OTTO__ARM_ENDPOINT`), and route it through the centralized
  // `appFetch` wrapper so it inherits the portal's local-vs-cloud handling, host
  // validation, and auth hardening instead of a raw tokenized fetch.
  const url = `${armEndpoint}${resourcePath}/freeGrantBalances?api-version=${apiVersions.otto}`;

  const data = await appFetch<FreeGrantBalance[] | { value?: FreeGrantBalance[] }>(url, token, {
    method: 'GET',
  });
  return Array.isArray(data) ? data : data.value ?? [];
};

/**
 * Validates an application name.
 * @param appName - The app name to validate
 * @param existingAppNames - Array of existing app names to check for duplicates
 * @returns Validation result with isValid flag and error message
 */
export const validateAppName = (appName: string, existingAppNames: string[]): AppNameValidationResult => {
  const trimmed = appName.trim();
  if (!trimmed) {
    return { isValid: false, error: '' }; // Empty - no error message shown yet
  }
  // Check format: alphanumeric, dashes, underscores, 1-64 chars
  const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;
  if (!validNamePattern.test(trimmed)) {
    return { 
      isValid: false, 
      error: 'Name must start with a letter and contain only letters, numbers, dashes, or underscores (max 64 chars)' 
    };
  }
  // Check for duplicate names
  const existingNamesLower = existingAppNames.map(name => name.toLowerCase());
  if (existingNamesLower.includes(trimmed.toLowerCase())) {
    return { isValid: false, error: `An application named "${trimmed}" already exists` };
  }
  return { isValid: true, error: '' };
};

export type CreateAutomationAppResult = {
  resource: unknown;
  statusUrl: string | null;
};

export const createAutomationApp = async (projectName: string, appName: string, _location: string, getToken: () => Promise<string | null>): Promise<CreateAutomationAppResult | undefined> => {
    const token = await getToken();
    if (!token) {
      console.error('Failed to get Otto token');
      return;
    }

    const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}?api-version=${apiVersions.otto}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;
      try {
        const errorResponse = errorText ? JSON.parse(errorText) : null;
        const error = errorResponse?.error ?? errorResponse;
        errorMessage = `Code: ${error?.code ?? 'Unknown'}, Message: ${error?.message ?? 'No message'}`;
      } catch {
        errorMessage = `Status: ${response.status}, Body: ${errorText || '(empty)'}`;
      }
      console.error('PUT request failed:', response.status, errorMessage);
      throw new Error(errorMessage);
    }

    const resource = await response.json();
    const statusUrl = response.headers.get('Azure-Asyncoperation');
    
    console.log('PUT request succeeded:', resource);
    console.log('Status URL:', statusUrl);
    
    return { resource, statusUrl };
};

export const deleteAutomationApp = async (projectName: string, appName: string, getToken: () => Promise<string | null>): Promise<boolean> => {
  try {
    const token = await getToken();
    if (!token) {
      console.error('Failed to get Otto token');
      return false;
    }

    const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}?api-version=${apiVersions.otto}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('DELETE request failed:', response.status, errorText);
      return false;
    }

    console.log('DELETE request succeeded');
    return true;
  } catch (error) {
    console.error('Error making DELETE request:', error);
    return false;
  }
};
